import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import type { BrandingConfig } from '@photobooth/types';

export function BrandingPage() {
  const [branding, setBranding] = useState<BrandingConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      const data = await api.branding.getBranding();
      setBranding(data);
    } catch (err) {
      console.error('Failed to fetch branding:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!branding) return;
    
    setIsSaving(true);
    try {
      await api.branding.updateBranding(branding);
      alert('Branding updated successfully');
    } catch (err) {
      alert('Failed to update branding');
    } finally {
      setIsSaving(false);
    }
  };

  const handleColorChange = (key: keyof BrandingConfig['colors'], value: string) => {
    if (!branding) return;
    setBranding({
      ...branding,
      colors: {
        ...branding.colors,
        [key]: value,
      },
    });
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !branding) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (PNG, JPG, etc.)');
      return;
    }
    setIsUploadingLogo(true);
    try {
      const result = await api.branding.uploadLogo(file);
      setBranding({
        ...branding,
        logos: { ...branding.logos, primary: result.url },
      });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to upload logo');
    } finally {
      setIsUploadingLogo(false);
      e.target.value = '';
    }
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!branding) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          Failed to load branding configuration
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-text">Branding</h1>
          <p className="text-text-muted mt-1">Customize the look and feel of your photo booth system</p>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-primary text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="bg-surface rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-text mb-4">Business Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text mb-1">Logo</label>
                <div className="flex items-center gap-4">
                  {branding.logos.primary && (
                    <img
                      src={branding.logos.primary}
                      alt="Logo"
                      className="h-16 w-auto object-contain border border-gray-200 rounded"
                    />
                  )}
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={isUploadingLogo}
                      className="block text-sm text-text-muted"
                    />
                    {isUploadingLogo && <p className="text-sm text-text-muted mt-1">Uploading...</p>}
                  </div>
                </div>
                <p className="text-xs text-text-muted mt-1">Used on public website and booth app. Max 5MB.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Business Name</label>
                <input
                  type="text"
                  value={branding.businessName}
                  onChange={(e) => setBranding({ ...branding, businessName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Tagline</label>
                <input
                  type="text"
                  value={branding.tagline || ''}
                  onChange={(e) => setBranding({ ...branding, tagline: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-text mb-4">Color Theme</h2>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(branding.colors).map(([key, value]) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-text mb-1 capitalize">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={value}
                      onChange={(e) => handleColorChange(key as keyof BrandingConfig['colors'], e.target.value)}
                      className="h-10 w-16 rounded border border-gray-300"
                    />
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => handleColorChange(key as keyof BrandingConfig['colors'], e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-surface rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-text mb-4">UI Customization</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text mb-1">Border Radius</label>
                <select
                  value={branding.ui.borderRadius}
                  onChange={(e) => setBranding({
                    ...branding,
                    ui: { ...branding.ui, borderRadius: e.target.value as BrandingConfig['ui']['borderRadius'] }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="none">None</option>
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                  <option value="full">Full</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Button Style</label>
                <select
                  value={branding.ui.buttonStyle}
                  onChange={(e) => setBranding({
                    ...branding,
                    ui: { ...branding.ui, buttonStyle: e.target.value as BrandingConfig['ui']['buttonStyle'] }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="flat">Flat</option>
                  <option value="gradient">Gradient</option>
                  <option value="outlined">Outlined</option>
                  <option value="soft">Soft</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Animation Level</label>
                <select
                  value={branding.ui.animationLevel}
                  onChange={(e) => setBranding({
                    ...branding,
                    ui: { ...branding.ui, animationLevel: e.target.value as BrandingConfig['ui']['animationLevel'] }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="none">None</option>
                  <option value="subtle">Subtle</option>
                  <option value="playful">Playful</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-surface rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-text mb-4">Print Settings</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-text">Watermark Enabled</label>
                <input
                  type="checkbox"
                  checked={branding.print.watermarkEnabled}
                  onChange={(e) => setBranding({
                    ...branding,
                    print: { ...branding.print, watermarkEnabled: e.target.checked }
                  })}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Watermark Position</label>
                <select
                  value={branding.print.watermarkPosition}
                  onChange={(e) => setBranding({
                    ...branding,
                    print: { ...branding.print, watermarkPosition: e.target.value as BrandingConfig['print']['watermarkPosition'] }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="top-left">Top Left</option>
                  <option value="top-right">Top Right</option>
                  <option value="bottom-left">Bottom Left</option>
                  <option value="bottom-right">Bottom Right</option>
                  <option value="center">Center</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Watermark Opacity (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={branding.print.watermarkOpacity}
                  onChange={(e) => setBranding({
                    ...branding,
                    print: { ...branding.print, watermarkOpacity: parseInt(e.target.value) }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Footer Text</label>
                <input
                  type="text"
                  value={branding.print.footerText || ''}
                  onChange={(e) => setBranding({
                    ...branding,
                    print: { ...branding.print, footerText: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-text">QR Code on Print</label>
                <input
                  type="checkbox"
                  checked={branding.print.qrCodeOnPrint}
                  onChange={(e) => setBranding({
                    ...branding,
                    print: { ...branding.print, qrCodeOnPrint: e.target.checked }
                  })}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-text mb-4">Preview</h2>
            <div 
              className="p-6 rounded-lg"
              style={{
                backgroundColor: branding.colors.background,
                color: branding.colors.text,
              }}
            >
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: branding.fonts.heading }}>
                {branding.businessName}
              </h3>
              {branding.tagline && (
                <p className="text-sm mb-4" style={{ color: branding.colors.textMuted }}>
                  {branding.tagline}
                </p>
              )}
              <button
                className="px-4 py-2 rounded text-white"
                style={{
                  backgroundColor: branding.colors.primary,
                  borderRadius: branding.ui.borderRadius === 'full' ? '9999px' : 
                               branding.ui.borderRadius === 'large' ? '1.5rem' :
                               branding.ui.borderRadius === 'medium' ? '0.75rem' :
                               branding.ui.borderRadius === 'small' ? '0.375rem' : '0',
                }}
              >
                Sample Button
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
